/**
 * Zerops YAML Builder Service
 *
 * Generates import YAML for the unified zcp control plane service.
 * The container is always the same `zcp@1` Ubuntu base — Coding Agent and
 * Cloud IDE are independent opt-in features layered on top via init commands.
 * Container setup is delegated to zcp subcommands (init, init sshfs, init nginx,
 * service start nginx, service start vscode).
 */

import { Injectable } from '@angular/core';
import { ZagentAgentType, ZagentAuthType, AGENT_PROVIDER_MAP } from '../zerops-services.model';
import { agentTypeToEnvSuffix } from '../zerops-services.utils';
import {
  PROVIDER_ENV_VAR_MAP,
  zcpInstallCommand
} from './shared-scripts.template';
import { environment } from '@zerops/zerops/env';

/**
 * One enabled agent's deploy config. Multiple of these form the multi-agent
 * input to the YAML builder.
 */
export interface YamlAgentConfig {
  agentType: ZagentAgentType;
  authType: ZagentAuthType;
  apiToken?: string;
}

/**
 * Multi-agent input shape for the YAML builder. Replaces the single-agent
 * shape — callers pass an `agents` list and the builder iterates.
 */
export interface ZeropsYamlConfig {
  serviceName: string;
  enableAgent: boolean;
  enableVscode: boolean;
  enablePublicAccess: boolean;
  vscodePassword?: string;
  selectedServices?: string[];
  agents?: YamlAgentConfig[];
}

@Injectable({ providedIn: 'root' })
export class ZeropsYamlBuilder {
  #zcpVersion = environment.zcpVersion?.startsWith('$$') ? undefined : environment.zcpVersion || undefined;

  /**
   * Generate import YAML for the unified zcp service.
   *
   * Bare container (both flags false) is a valid deployment: terminal/SSH/VPN
   * always work via the base zcp@1 image.
   */
  generate(config: ZeropsYamlConfig): string {
    return this.#generateZcpYaml(config);
  }

  #generateZcpYaml(config: ZeropsYamlConfig): string {
    const {
      serviceName,
      enableAgent,
      enableVscode,
      agents = [],
      enablePublicAccess,
      vscodePassword,
      selectedServices = []
    } = config;

    const activeAgents = enableAgent ? agents : [];

    return this.#assembleYaml({
      serviceName,
      enablePublicAccess: enablePublicAccess && enableVscode,
      initCommands: this.#buildInitCommands({
        enableAgent,
        enableVscode,
        agents: activeAgents,
        selectedServices
      }),
      vault: this.#buildVault({
        enableAgent,
        enableVscode,
        agents: activeAgents,
        enablePublicAccess,
        vscodePassword,
        selectedServices
      }),
      includePorts: enableVscode
    });
  }

  /**
   * Build initCommands based on which features are enabled
   */
  #buildInitCommands(c: {
    enableAgent: boolean;
    enableVscode: boolean;
    agents: YamlAgentConfig[];
    selectedServices: string[];
  }): string[] {
    const commands: string[] = [];

    // Install zcp
    commands.push(zcpInstallCommand(this.#zcpVersion));

    // zcp init handles base setup: SSH config, git config, agent configs
    // (driven by per-agent ZCP_AGENT_AUTH_TYPE_<SUFFIX> + ZCP_PROVIDER_<SUFFIX>),
    // and VS Code settings (when ZCP_VSCODE=true).
    commands.push('zcp init');

    // Agent-specific config for opencode-ai (claude-code is handled inside `zcp init`)
    const opencode = c.agents.find((a) => a.agentType === 'opencode-ai');
    if (c.enableAgent && opencode) {
      const provider = AGENT_PROVIDER_MAP['opencode-ai'];
      const model = provider === 'anthropic'
        ? 'claude-3-5-sonnet-latest'
        : provider === 'openai'
        ? 'gpt-4o'
        : 'default';

      commands.push(
        'mkdir -p ~/.config/opencode',
        `echo '{"theme": "dark", "telemetry": false, "provider": "${provider}", "model": "${model}", "autoSave": true, "fontSize": 14, "tabSize": 2}' > ~/.config/opencode/config.json`
      );
    }

    // SSHFS mounting via zcp
    if (c.selectedServices.length > 0) {
      commands.push('zcp init sshfs');
    }

    // Nginx proxy config via zcp (only when VS Code is enabled)
    if (c.enableVscode) {
      commands.push('sudo -E zcp init nginx');
    }

    return commands;
  }

  /**
   * Build the `vault` env block based on which features are enabled.
   * Plain scalar values default to NON-sensitive — required here, because the
   * FE reads all of these back from plain list content (hasVscode/authorized
   * detection, /zcp-auth iframe URL); an `envSecrets` block would create them
   * as sensitive and the list API would return them redacted. A value can be
   * made sensitive with the `{ value, sensitive: true }` object form if ever
   * needed.
   */
  #buildVault(c: {
    enableAgent: boolean;
    enableVscode: boolean;
    agents: YamlAgentConfig[];
    enablePublicAccess: boolean;
    vscodePassword?: string;
    selectedServices: string[];
  }): Record<string, string> {
    const vault: Record<string, string> = {};

    // VS Code auth — write password AND the auth-enabled flag together.
    // Aligns with the post-deploy "Enable public access" path which adds
    // both `VSCODE_PASSWORD` and `ZCP_VSCODE_AUTH_ENABLED='true'`; if the
    // runtime reads the latter as the gate, deploy-time public access
    // would otherwise come up authless.
    if (c.enableVscode && c.enablePublicAccess && c.vscodePassword) {
      vault['VSCODE_PASSWORD'] = `"${c.vscodePassword}"`;
      vault['ZCP_VSCODE_AUTH_ENABLED'] = '"true"';
    }

    // VS Code flag
    if (c.enableVscode) {
      vault['ZCP_VSCODE'] = '"true"';
    }

    // SSHFS hostnames
    if (c.selectedServices.length > 0) {
      vault['ZCP_SSHFS_HOSTNAMES'] = `"${c.selectedServices.join(',')}"`;
    }

    // Agent metadata — unified `ZCP_AGENT_*_<SUFFIX>` per-agent schema.
    // Per-agent vars written here:
    //   - `ZCP_AGENT_AUTH_TYPE_<SUFFIX>` — declared mode at deploy time
    //   - `ZCP_AGENT_TOKEN_<SUFFIX>`     — token value (token mode only)
    // `ZCP_PROVIDER_<SUFFIX>` is no longer written — the agent → provider
    // map is a static 4-line table that's hardcoded everywhere (frontend,
    // recipe `zcp init`), so persisting it in userData was pure noise
    // that could drift if the map ever changed on either side.
    // The provider-canonical CLI key (`ANTHROPIC_API_KEY` / `OPENAI_API_KEY`
    // / etc) is written as a zerops env reference — `"${ZCP_AGENT_TOKEN_*}"`
    // — so the source of truth lives in one place. Updating the token in
    // userData propagates to the CLI env automatically on next restart.
    if (c.enableAgent && c.agents.length > 0) {
      for (const a of c.agents) {
        const suffix = agentTypeToEnvSuffix(a.agentType);
        vault[`ZCP_AGENT_AUTH_TYPE_${suffix}`] = `"${a.authType}"`;
      }

      // Per-agent API tokens (token mode only). We write the value into
      // `ZCP_AGENT_TOKEN_<SUFFIX>` (single source of truth) AND reference
      // it from the provider-canonical CLI env. If two agents in token
      // mode share a provider, the later CLI-key write wins (flagged as
      // a future product question — same as pre-rename behavior).
      for (const a of c.agents) {
        if (a.authType === 'token' && a.apiToken) {
          const suffix = agentTypeToEnvSuffix(a.agentType);
          const provider = AGENT_PROVIDER_MAP[a.agentType];
          const cliEnv = PROVIDER_ENV_VAR_MAP[provider] ?? 'API_KEY';
          vault[`ZCP_AGENT_TOKEN_${suffix}`] = `"${a.apiToken}"`;
          vault[cliEnv] = `"\${ZCP_AGENT_TOKEN_${suffix}}"`;
        }
      }
    }

    return vault;
  }

  /**
   * Assemble final YAML string
   */
  #assembleYaml(params: {
    serviceName: string;
    enablePublicAccess: boolean;
    initCommands: string[];
    vault: Record<string, string>;
    includePorts: boolean;
  }): string {
    const {
      serviceName,
      enablePublicAccess,
      initCommands,
      vault,
      includePorts
    } = params;

    const hasVscode = includePorts;

    // Build YAML header
    let yaml = `services:
  - hostname: ${serviceName}
    type: zcp@1
    maxContainers: 1${enablePublicAccess ? '\n    enableSubdomainAccess: true' : ''}
    verticalAutoscaling:
      minRam: 2`;

    // Add vault envs (plain scalars — non-sensitive by default)
    if (Object.keys(vault).length > 0) {
      yaml += `
    vault:`;
      for (const [ key, value ] of Object.entries(vault)) {
        yaml += `
      ${key}: ${value}`;
      }
    }

    // Add zeropsYaml section
    yaml += `
    zeropsYaml:
      zerops:
        - setup: ${serviceName}`;

    // Add run section
    yaml += `
          run:
            base: zcp@1`;

    // Add initCommands
    if (initCommands.length > 0) {
      yaml += `
            initCommands:`;
      for (const cmd of initCommands) {
        if (cmd.includes('{') || cmd.includes('"') || cmd.includes('\n')) {
          yaml += `
              - |
                ${cmd.split('\n').join('\n                ')}`;
        } else {
          yaml += `
              - ${cmd}`;
        }
      }
    }

    // Add ports
    if (includePorts) {
      yaml += `
            ports:
              - port: 8080
                httpSupport: true`;
    }

    // Add start command(s)
    if (hasVscode) {
      // zcp service wrappers for nginx + code-server
      yaml += `
            startCommands:
              - command: zcp service start nginx
                name: nginx
              - command: zcp service start vscode
                name: vscode`;
    } else {
      yaml += `
            start: zsc noop`;
    }

    return yaml;
  }
}
